/**
 * How wide a grid column starts out.
 *
 * A column's width is a statement about the values it holds. An id or a count
 * is a handful of digits and needs only enough room for its own title; a name
 * or an address is open-ended and is the only thing worth giving the table's
 * leftover space to. Sizing every column alike is what left a four-digit id as
 * wide as the name beside it while the name itself had nowhere to grow.
 *
 * So the type decides the shape and the descriptor decides the exceptions: a
 * declared `flex` or `width` is the backend's own call and is taken verbatim —
 * `width` fixes the column, as the descriptor's contract says it does, rather
 * than being re-read as a growth rate. Headers wrap rather than truncating (the
 * DataGrid theme override owns that), so a column narrower than its title still
 * states it in full.
 *
 * Kept apart from the renderers so the rule is one pure function rather than a
 * branch inside a module full of JSX.
 */

/** Column types whose values are numeric: a few digits, never prose. */
export const NUMERIC_TYPES = new Set(['integer', 'number']);

/**
 * The width one unit of flex is worth, and the floor for a flexing column that
 * names no width of its own.
 */
export const FLEX_UNIT = 120;

// A header-sized column has to hold the label text, the sort arrow and menu
// button that sit beside it, and the cell's own horizontal padding. The header
// font is the grid's small semi-bold label, where a character averages a little
// under 8px.
const CHAR_WIDTH_PX = 7.5;
const HEADER_CONTROLS_PX = 32;
const CELL_PADDING_PX = 20;

// Bounds for a column sized to a label. The floor keeps a one-character header
// from producing a column too narrow to grab; the ceiling keeps a verbose
// header from handing a two-digit count a quarter of the table, since the
// header wraps to a second line instead.
const LABEL_MIN_WIDTH = 72;
const LABEL_MAX_WIDTH = 200;

// Types whose values have a known printed shape, so the column is fixed at what
// that shape needs rather than at what its header says.
const BOOLEAN_WIDTH = 90;
const DATETIME_WIDTH = 150;
const ENUM_WIDTH = 140;

/** The floor for free text, which is the only type that flexes by default. */
const TEXT_MIN_WIDTH = 160;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Pixel width a label needs, bounded by LABEL_MIN_WIDTH/LABEL_MAX_WIDTH.
 *
 * @param {?string} label
 * @returns {number}
 */
export function labelWidth(label) {
  const chars = typeof label === 'string' ? label.length : 0;
  return clamp(
    Math.ceil(chars * CHAR_WIDTH_PX) + HEADER_CONTROLS_PX + CELL_PADDING_PX,
    LABEL_MIN_WIDTH,
    LABEL_MAX_WIDTH,
  );
}

/** The type's own sizing, before the descriptor's overrides are applied. */
function sizingForType(type, headerName, optionLabels) {
  if (NUMERIC_TYPES.has(type)) return { width: labelWidth(headerName) };
  if (type === 'boolean') return { width: BOOLEAN_WIDTH };
  if (type === 'datetime') return { width: DATETIME_WIDTH };
  if (type === 'enum') {
    const widest = optionLabels?.length ? Math.max(...optionLabels.map(labelWidth)) : ENUM_WIDTH;
    return { width: Math.max(widest, labelWidth(headerName)) };
  }
  // An actions cluster is sized by how many buttons it holds, which only the
  // renderer knows; it states its own width and wants no floor from here.
  if (type === 'actions') return {};
  // string, reference and anything the descriptor gains later: open-ended
  // values, so they take the leftover space.
  return { flex: 1, minWidth: TEXT_MIN_WIDTH };
}

/**
 * Resolve a descriptor column's sizing into MUI's terms.
 *
 * @param {object} declared - the descriptor's sizing, as the normalizer emits it.
 * @param {string} [declared.type] - the descriptor column type.
 * @param {?number} [declared.width] - a fixed width the column must take.
 * @param {?number} [declared.minWidth] - a floor the column must not go below.
 * @param {?number} [declared.flex] - a share of the leftover space.
 * @param {object} [context]
 * @param {?string} [context.headerName] - the resolved header text, which a
 *   column with no values to size against is sized to instead.
 * @param {string[]} [context.optionLabels] - resolved enum option labels.
 * @returns {{width?: number, minWidth?: number, flex?: number}} Sizing to
 *   spread onto the column definition.
 */
export function resolveColumnSizing({ type, width, minWidth, flex } = {}, { headerName, optionLabels } = {}) {
  // An explicit flex or width is the descriptor's own call, and the two are
  // mutually exclusive on the wire.
  if (flex != null) return { flex, minWidth: minWidth ?? FLEX_UNIT };
  if (width) return minWidth ? { width: Math.max(width, minWidth), minWidth } : { width };

  const sizing = sizingForType(type, headerName, optionLabels);
  if (minWidth) {
    if (sizing.width) return { width: Math.max(sizing.width, minWidth), minWidth };
    return { ...sizing, minWidth };
  }
  return sizing;
}
